import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/useAuth';
import { createBranch, getBranches, updateBranch } from '@/features/operations/api';
import type { BranchItem, UpdateBranchInput } from '@/features/operations/types';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

/// What the operator is editing, keyed by branch id. Held as the strings they typed so a
/// half-finished edit is never saved on a keystroke.
type BranchDraft = {
  name: string;
  city: string;
  address: string;
  phone: string;
  email: string;
};

type BranchDrafts = Record<string, BranchDraft>;

const emptyDraft: BranchDraft = { name: '', city: '', address: '', phone: '', email: '' };

function toDraft(branch: BranchItem): BranchDraft {
  return {
    name: branch.name,
    city: branch.city,
    address: branch.address,
    phone: branch.phone || '',
    email: branch.email || '',
  };
}

function buildDrafts(branches: BranchItem[]): BranchDrafts {
  return branches.reduce<BranchDrafts>((drafts, branch) => {
    drafts[branch.id] = toDraft(branch);
    return drafts;
  }, {});
}

/// Says what this branch holds, which is what decides whether it can be closed at all.
function describeHolding(branch: BranchItem) {
  if (!branch.deviceCount && !branch.userCount) {
    return 'No tiene equipos ni personal, así que se puede desactivar.';
  }

  const held = [
    branch.deviceCount ? `${branch.deviceCount} equipo${branch.deviceCount > 1 ? 's' : ''}` : null,
    branch.userCount ? `${branch.userCount} usuario${branch.userCount > 1 ? 's' : ''}` : null,
  ].filter(Boolean);

  return `Tiene ${held.join(' y ')}, así que no se puede desactivar.`;
}

/**
 * Where PhoneTec operates. A branch is never deleted: the devices received there name it, and
 * the phone that was standing at a branch was standing there whatever happens afterwards.
 */
export function AdminBranchesPage() {
  const { token } = useAuth();

  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [drafts, setDrafts] = useState<BranchDrafts>({});
  const [newBranch, setNewBranch] = useState<BranchDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const loaded = await getBranches(token);
        setBranches(loaded);
        setDrafts(buildDrafts(loaded));
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'No pudimos cargar las sucursales.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token]);

  if (!token) {
    return null;
  }

  const refresh = async () => {
    const loaded = await getBranches(token);
    setBranches(loaded);
    setDrafts(buildDrafts(loaded));
  };

  const run = async (action: () => Promise<void>, successText: string) => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await action();
      setSuccessMessage(successText);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  const setNewField = (field: keyof BranchDraft) => (value: string) =>
    setNewBranch((current) => ({ ...current, [field]: value }));

  const setDraftField = (branchId: string, field: keyof BranchDraft) => (value: string) =>
    setDrafts((current) => ({
      ...current,
      [branchId]: { ...current[branchId], [field]: value },
    }));

  const handleCreate = () =>
    run(async () => {
      if (!newBranch.name.trim() || !newBranch.city.trim() || !newBranch.address.trim()) {
        throw new Error('Cargá el nombre, la ciudad y la dirección de la sucursal.');
      }

      await createBranch(token, {
        name: newBranch.name.trim(),
        city: newBranch.city.trim(),
        address: newBranch.address.trim(),
        phone: newBranch.phone.trim() || undefined,
        email: newBranch.email.trim() || undefined,
      });
      setNewBranch(emptyDraft);
      await refresh();
    }, 'Sucursal creada.');

  const handleSave = (branch: BranchItem) =>
    run(async () => {
      const draft = drafts[branch.id] || toDraft(branch);

      if (!draft.name.trim() || !draft.city.trim() || !draft.address.trim()) {
        throw new Error('El nombre, la ciudad y la dirección no pueden quedar vacíos.');
      }

      // An emptied phone or email goes as null, which is what clears it. The rest is sent as
      // typed; the backend decides whether any of it is actually a change.
      const input: UpdateBranchInput = {
        name: draft.name.trim(),
        city: draft.city.trim(),
        address: draft.address.trim(),
        phone: draft.phone.trim() || null,
        email: draft.email.trim() || null,
      };

      await updateBranch(token, branch.id, input);
      await refresh();
    }, 'Sucursal actualizada.');

  const handleToggleActive = (branch: BranchItem) =>
    run(
      async () => {
        await updateBranch(token, branch.id, { isActive: !branch.isActive });
        await refresh();
      },
      branch.isActive ? 'Sucursal desactivada.' : 'Sucursal reactivada.',
    );

  const activeCount = branches.filter((branch) => branch.isActive).length;

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CONFIGURACIÓN"
        title="Sucursales"
        description="Dónde opera PhoneTec. Al recibir un equipo hay que decir en qué sucursal queda, así que sin ninguna activa no se puede registrar ninguna recepción."
        badge="Solo administración"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <SectionCard
            title="Agregar una sucursal"
            description="Se crea activa. El teléfono y el email son opcionales."
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Nombre"
                  value={newBranch.name}
                  onChange={(event) => setNewField('name')(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  label="Ciudad"
                  value={newBranch.city}
                  onChange={(event) => setNewField('city')(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  required
                  label="Dirección"
                  value={newBranch.address}
                  onChange={(event) => setNewField('address')(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={newBranch.phone}
                  onChange={(event) => setNewField('phone')(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  value={newBranch.email}
                  onChange={(event) => setNewField('email')(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <PrimaryButton onClick={handleCreate} disabled={isSaving}>
                  Agregar sucursal
                </PrimaryButton>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Sucursales registradas"
            description="Las sucursales no se borran. Una con equipos o personal tampoco se desactiva: los equipos que la nombran tienen que seguir diciendo dónde estuvieron."
          >
            {branches.length === 0 ? (
              <Alert severity="info">
                Todavía no hay sucursales cargadas. Hasta que exista una, no se puede registrar la
                recepción de ningún equipo.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {activeCount === 0 ? (
                  <Alert severity="warning">
                    No hay ninguna sucursal activa. No se puede recibir ningún equipo hasta que
                    actives al menos una.
                  </Alert>
                ) : null}

                {branches.map((branch, index) => (
                  <Box key={branch.id}>
                    {index > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={`Nombre de ${branch.name}`}
                          value={drafts[branch.id]?.name ?? ''}
                          onChange={(event) => setDraftField(branch.id, 'name')(event.target.value)}
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label={`Ciudad de ${branch.name}`}
                          value={drafts[branch.id]?.city ?? ''}
                          onChange={(event) => setDraftField(branch.id, 'city')(event.target.value)}
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label={`Dirección de ${branch.name}`}
                          value={drafts[branch.id]?.address ?? ''}
                          onChange={(event) =>
                            setDraftField(branch.id, 'address')(event.target.value)
                          }
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={`Teléfono de ${branch.name}`}
                          value={drafts[branch.id]?.phone ?? ''}
                          onChange={(event) =>
                            setDraftField(branch.id, 'phone')(event.target.value)
                          }
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={`Email de ${branch.name}`}
                          value={drafts[branch.id]?.email ?? ''}
                          onChange={(event) =>
                            setDraftField(branch.id, 'email')(event.target.value)
                          }
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <PrimaryButton
                            size="small"
                            onClick={() => handleSave(branch)}
                            disabled={isSaving}
                          >
                            Guardar sucursal
                          </PrimaryButton>
                          <PrimaryButton
                            size="small"
                            color="secondary"
                            onClick={() => handleToggleActive(branch)}
                            disabled={isSaving}
                          >
                            {branch.isActive ? 'Desactivar' : 'Reactivar'}
                          </PrimaryButton>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={branch.isActive ? 'Operativa' : 'Cerrada'}
                            color={branch.isActive ? 'success' : 'default'}
                            variant={branch.isActive ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {describeHolding(branch)} Última modificación:{' '}
                            {new Date(branch.updatedAt).toLocaleString('es-UY')}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </>
      )}
    </Stack>
  );
}
